import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { verifyPaymentSuccessToken } from "@/http/paymentApi";
import { HOME_ROUTE, LOGIN_ROUTE, PURCHASE_HISTORY } from "@/utils/const";
import { formatCurrency } from "@/utils/formatters";

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PaymentsSuccess = () => {
  const { isAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("d");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["payment-success-token", token],
    queryFn: () => verifyPaymentSuccessToken(token as string),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-6 p-6 text-center">
          <CheckCircle2 className="mx-auto h-16 w-16 text-green-500" />
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-gray-900">Оплата прошла</h1>
            <p className="text-sm text-gray-600">
              Спасибо! Платёж получен. Подробности появятся в истории покупок в течение нескольких
              минут.
            </p>
          </div>

          {token && isLoading && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Загрузка деталей…
            </div>
          )}

          {data && (
            <dl className="space-y-2 rounded-lg border bg-gray-50 p-4 text-left text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Покупка</dt>
                <dd className="text-right font-medium text-gray-900">{data.title}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Сумма</dt>
                <dd className="text-right font-semibold text-primary">
                  {formatCurrency(data.amount)}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Филиал</dt>
                <dd className="text-right text-gray-900">{data.club_title}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Дата</dt>
                <dd className="text-right text-gray-900">{formatDateTime(data.created_at)}</dd>
              </div>
            </dl>
          )}

          {token && isError && (
            <p className="text-xs text-red-500">Не удалось проверить детали платежа.</p>
          )}

          <div className="flex flex-col gap-2">
            {isAuth ? (
              <>
                <Button asChild>
                  <Link to={PURCHASE_HISTORY}>История покупок</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to={HOME_ROUTE}>На главную</Link>
                </Button>
              </>
            ) : (
              <Button asChild>
                <Link to={LOGIN_ROUTE}>Войти в кабинет</Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsSuccess;
